import { PexelsSearch } from 'libs/e1api/imagesearch';
import clonedeep from 'lodash.clonedeep';
import { WidgetId } from 'types/idTypes';
import { PexelsPhoto, PexelsSearchResponse } from 'types/imageLibsTypes';
import { InfographState } from 'types/infographTypes';
import { WidgetType } from 'types/widget.types';
import { ImageWidgetData } from 'widgets/ImageWidget/ImageWidget.types';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';

type ImagesList = PexelsPhoto[] | null;

type PexelsSearchCache = { [query: string]: PexelsSearchResponse };

const IMAGES_PER_KEYWORD = 10;

const PEXELS_IMAGE_DIMENSIONS = {
  tiny: { width: 280, height: 200 },
  large: { width: 940, height: 650 },
  portrait: { width: 800, height: 1200 },
  landscape: { width: 1200, height: 627 },
  large2x: { width: 940 * 2, height: 650 * 2 }, // dpr=2, use this src when no other options fit
};

const CROPPED_IMAGE_LABELS = ['tiny', 'portrait', 'landscape'];

const WH_CUT_OFF_RATIO = 1.6; // image height / width or width / height > 1.6, then we consider using landscape or portrait src image from pexels

/**
 * Replace images in an infograph with icons from pexels
 * Replaceable images are specified by replaceables array.
 * Uses `imageKeywords` to search for icons to replace with.
 *
 * @param infograph Infograph to update
 * @param replaceables Image widgets to replace
 * @param imageKeywords Keywords to search for images
 * @returns InfographState
 */
export const replaceImages = async (infograph: InfographState, replaceables: WidgetId[], imageKeywords: string[]) => {
  // There was an issue generating keywords for the icons or
  // The template does not have any icons to replace
  if (!imageKeywords?.length || !replaceables?.length) {
    return infograph;
  }

  const updatedInfograph = clonedeep(infograph);
  const { widgets: widgetMap } = updatedInfograph;

  // Get one icon for each replaceable widget
  const imagesList = await getImages(replaceables.length, imageKeywords);
  // some search keyword might return null, filter those out
  const filteredImagesList = imagesList.filter((images) => images && images.length) as PexelsPhoto[][];

  // No images to replace
  if (!filteredImagesList.length) {
    return updatedInfograph;
  }

  // Replace images with pexels search results
  for (let i = 0; i < replaceables.length; i++) {
    const widgetId = replaceables[i];
    if (getWidgetTypeFromId(widgetId) !== WidgetType.Image) {
      continue;
    }

    const widget = widgetMap[widgetId] as ImageWidgetData;
    if (!widget) {
      continue;
    }

    // pick a image from search results
    const imageListIdx = i < filteredImagesList.length ? i : getRandomNumber(filteredImagesList.length);
    const imageData = filteredImagesList[imageListIdx][getRandomNumber(IMAGES_PER_KEYWORD)];

    widgetMap[widgetId] = updateImageData(widget, imageData);
  }

  return updatedInfograph as InfographState;
};

/**
 * Update the image widget with the new image data from pexels search result
 *
 * @param widget original image widget to be updated
 * @param imageData imageData from search result
 * @returns updated image widget
 */
export const updateImageData = (widget: ImageWidgetData, imageData: PexelsPhoto): ImageWidgetData => {
  const { widthPx, heightPx } = widget;
  const pexelsImageSrcLabel = getPexelsImageSrcLabel(widthPx, heightPx);
  // tiny, landscape and portrait images are cropped in a way that it's WH ratio is not the same as the original image, use the cropped image size to update the image widget
  if (CROPPED_IMAGE_LABELS.includes(pexelsImageSrcLabel)) {
    imageData.width = PEXELS_IMAGE_DIMENSIONS[pexelsImageSrcLabel].width;
    imageData.height = PEXELS_IMAGE_DIMENSIONS[pexelsImageSrcLabel].height;
  }

  const originalImageWHRatio = widthPx / heightPx;
  const replaceImageWHRatio = imageData.width / imageData.height;
  if (originalImageWHRatio === replaceImageWHRatio) {
    return {
      ...widget,
      url: imageData.src[pexelsImageSrcLabel],
      imageRect: { widthPx, heightPx },
    };
  } else if (originalImageWHRatio > replaceImageWHRatio) {
    // scale the replace image width to be the same as the original image, the height would be longer than the original image
    const scaledImageRectHeight = imageData.height * (widthPx / imageData.width);
    return {
      ...widget,
      url: imageData.src[pexelsImageSrcLabel],
      crop: {
        top: (scaledImageRectHeight - heightPx) / 2,
        bottom: (scaledImageRectHeight - heightPx) / 2,
        left: 0,
        right: 0,
      },
      imageRect: { widthPx, heightPx: scaledImageRectHeight },
    };
  } else {
    // scale the replace image height to be the same as the original image, the width would be longer than the original image
    const scaledImageRectWidth = imageData.width * (heightPx / imageData.height);
    return {
      ...widget,
      url: imageData.src[pexelsImageSrcLabel],
      crop: {
        top: 0,
        bottom: 0,
        left: (scaledImageRectWidth - widthPx) / 2,
        right: (scaledImageRectWidth - widthPx) / 2,
      },
      imageRect: { widthPx: scaledImageRectWidth, heightPx },
    };
  }
};

/**
 * Perform parrallel image search to pexels based on keywords
 *
 * @param numImages number of images we want to get
 * @param imageKeywords keywords to be used for image search
 * @returns list containing `numImages` of photos list
 */
const getImages = async (numImages: number, imageKeywords: string[]): Promise<(ImagesList | null)[]> => {
  const NUM_PARALLEL_REQUESTS = 4;
  const MAX_TRY_EACH_KEYWORD = 2;
  let imagesList: ImagesList[] = [];

  const pexelsSearchCache = {};

  let promiseList: Promise<ImagesList>[] = [];

  // Perform image searches in parallel
  for (let i = 0; i < numImages; i++) {
    promiseList.push(retryImageSearch(i, imageKeywords, numImages, MAX_TRY_EACH_KEYWORD, pexelsSearchCache));
    if (promiseList.length >= NUM_PARALLEL_REQUESTS || i === numImages - 1) {
      const imagesSearchResult = await Promise.all(promiseList);
      imagesList = imagesList.concat(imagesSearchResult);
      promiseList = [];
    }
  }
  return imagesList;
};

/**
 * Search the image based on keyword and retry if failed
 */
const retryImageSearch = async (
  keywordIdx: number,
  imageKeywords: string[],
  numImages: number,
  retries: number,
  pexelsSearchCache: PexelsSearchCache,
): Promise<ImagesList> => {
  const keyword = imageKeywords[keywordIdx];
  try {
    const searchParams = {
      per_page: IMAGES_PER_KEYWORD,
    };
    const params = { ...searchParams, query: keyword };
    const result = pexelsSearchCache[keyword] || (await PexelsSearch(params));
    pexelsSearchCache[keyword] = result;

    return result.photos;
  } catch (error) {
    if (retries > 0) {
      console.log(`Failed to search with keyword '${keyword}'. Retrying with a different keyword...`);
      const newKeywordIndex = (keywordIdx + numImages) % imageKeywords.length;
      return retryImageSearch(newKeywordIndex, imageKeywords, numImages, retries - 1, pexelsSearchCache);
    }

    return null;
  }
};

/**
 * choose the correct image src for the pexels image based on original image width and height
 * @param width number
 * @param height number
 * @returns 'tiny' | 'landscape' | 'portrait' | 'large' | 'large2x'
 */
const getPexelsImageSrcLabel = (
  width: number,
  height: number,
): 'tiny' | 'landscape' | 'portrait' | 'large' | 'large2x' => {
  if (width < PEXELS_IMAGE_DIMENSIONS.tiny.width && height < PEXELS_IMAGE_DIMENSIONS.tiny.height) {
    return 'tiny';
  }

  if (
    width / height > WH_CUT_OFF_RATIO &&
    width < PEXELS_IMAGE_DIMENSIONS.landscape.width &&
    height < PEXELS_IMAGE_DIMENSIONS.landscape.height
  ) {
    return 'landscape';
  }

  if (
    height / width > WH_CUT_OFF_RATIO &&
    width < PEXELS_IMAGE_DIMENSIONS.portrait.width &&
    height < PEXELS_IMAGE_DIMENSIONS.portrait.height
  ) {
    return 'portrait';
  }

  if (width < PEXELS_IMAGE_DIMENSIONS.large.width && height < PEXELS_IMAGE_DIMENSIONS.large.height) {
    return 'large';
  }

  return 'large2x';
};

/**
 * generate a random number between 0 and n-1
 * @param n number
 * @returns number
 */
const getRandomNumber = (n: number): number => {
  return Math.floor(Math.random() * n);
};
